import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';

import { ANALYTICS_EVENTS, UNIFIED } from 'src/app/analytics/analyticsEvents';
import * as paths from 'src/app/router/paths';
import { redirectToOnboarding, segmentAnalyticsEvent, segmentTrack } from 'src/app/state/actions';
import {
  getCurrentPathname,
  getOnboardingCurrentIndex,
  getOnboardingProgressFillPercent,
  getOnboardingStepsLength,
} from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import { IS_MOBILE } from 'src/consts';

import { NavTheme, Variant } from '../constants';
import { HeaderWrapper } from '../styles';
import styles from '../styles.module.scss';

import { DesktopHeader, LoggedOutSideMenu, MobileHeader } from './components';

interface LoggedOutHeaderProps {
  navOpacity?: number; // Defaults to 1, opacity for scrolling transition
  navTheme?: NavTheme; // Nav color theme blue or white, defaults to white
  hasSignInLink?: boolean;
  hasGetStartedLink?: boolean;
  stickyTopOffset?: number; // Defaults to 0, offset for scrolling transition
  variant?: Variant; // Navigation variant enum, defaults to base
}

export const LoggedOutHeader = ({
  navOpacity = 1,
  navTheme = NavTheme.white,
  hasSignInLink = true,
  hasGetStartedLink = true,
  stickyTopOffset = 0,
  variant = Variant.base,
}: LoggedOutHeaderProps) => {
  const currentStepIndex = useSelector(getOnboardingCurrentIndex);
  const pathname = useSelector(getCurrentPathname);
  const progressBarFillPercent = useSelector(getOnboardingProgressFillPercent);
  const progressBarTotalSteps = useSelector(getOnboardingStepsLength);
  const dispatch = useDispatch();

  const [showMenu, setShowMenu] = useState(false);

  const isHowToPage = /^\/how-to\/?$/.test(pathname);
  const isOnboardingFlow = pathname === paths.getStarted.toPath();
  const isWelcomePage = pathname === paths.welcome.toPath();

  useEffect(() => {
    setShowMenu(false);
  }, [pathname]);

  const handleToggleMenu = (e: MouseEvent) => {
    e.preventDefault();
    setShowMenu(!showMenu);

    dispatch(segmentTrack(UNIFIED.viewNavbar, { pagepath: pathname, is_logged_in: false }));
  };

  const handleCloseMenu = () => {
    setShowMenu(false);
  };

  const handleGetStartedClick = () => {
    const attributes = { isMobile: IS_MOBILE ? 'yes' : 'no' };
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.headerGetStartedClicked, { attributes }));
    dispatch(redirectToOnboarding('Navigation Bar'));
  };

  const getRedirectClickHandler = (handler: () => void) => (e: MouseEvent) => {
    e.preventDefault();
    handler();
  };

  return (
    <HeaderWrapper className={styles.component}>
      <DesktopHeader
        currentStepIndex={currentStepIndex}
        getRedirectClickHandler={getRedirectClickHandler}
        handleGetStartedClick={handleGetStartedClick}
        isHowToPage={isHowToPage}
        isOnboardingFlow={isOnboardingFlow}
        navOpacity={navOpacity}
        navTheme={navTheme}
        progressBarFillPercent={progressBarFillPercent}
        progressBarTotalSteps={progressBarTotalSteps}
        hasSignInLink={hasSignInLink}
        hasGetStartedLink={hasGetStartedLink}
        showMenu={showMenu}
        stickyTopOffset={stickyTopOffset}
        variant={variant}
      />

      <MobileHeader
        currentStepIndex={currentStepIndex}
        handleToggleMenu={handleToggleMenu}
        isOnboardingFlow={isOnboardingFlow}
        isWelcomePage={isWelcomePage}
        navOpacity={navOpacity}
        navTheme={navTheme}
        progressBarFillPercent={progressBarFillPercent}
        progressBarTotalSteps={progressBarTotalSteps}
        showMenu={showMenu}
        stickyTopOffset={stickyTopOffset}
      />

      <LoggedOutSideMenu
        handleCloseMenu={handleCloseMenu}
        showMenu={showMenu}
        hasSignInLink={hasSignInLink}
        hasGetStartedLink={hasGetStartedLink}
      />
    </HeaderWrapper>
  );
};
